/**
 * Crowpeaks design tokens — single source of truth for the UI design system.
 *
 * Covers: Color, Shadow, Radius, Spacing, Typography, Layout, Borders & focus,
 * Components (states), Motion, Iconography, Brand mark.
 *
 * The same values drive:
 *  1. The Ant Design theme config (src/theme.ts)
 *  2. CSS custom properties (--cp-*) consumed by static CSS
 */

export type ColorMode = 'light' | 'dark';

export interface TokenPalette {
  /** Brand / primary accent. */
  brand: string;
  /** Darker brand for hover/pressed primary. */
  highlight: string;
  /** Main text color. */
  text: string;
  /** Muted / secondary text. */
  muted: string;
  /** Form labels and tertiary text. */
  label: string;
  /** Section / group label text. */
  sectionLabel: string;
  link: string;
  /** Subtle surface border. */
  border: string;
  /** Stronger border for inputs and dividers. */
  fieldBorder: string;
  /** Strongest border. */
  borderStrong: string;
  /** App background (layout). */
  background: string;
  /** Panel / card surface. */
  surface: string;
  /** Sidebar / shell surface. */
  shell: string;
  /** Hover fill. */
  hover: string;
  /** Pressed / active fill. */
  active: string;
  /** Selected background tint. */
  selectedBg: string;
  /** Focus outline color. */
  focus: string;
  /** Focus ring (spread) color with alpha. */
  focusRing: string;
  error: string;
  warning: string;
  success: string;
  info: string;
  errorBg: string;
  warningBg: string;
  successBg: string;
  infoBg: string;
  readOnlyFieldBg: string;
  fieldShadow: string;
  fieldHoverShadow: string;
}

export type BadgeVariant = 'default' | 'info' | 'success' | 'warning' | 'error' | 'neutral';

export const appFontFamily =
  "'Geist', -apple-system-body, ui-sans-serif, -apple-system, system-ui, 'Segoe UI', Helvetica, 'Apple Color Emoji', Arial, sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol'";

export const appFontFamilyMono =
  "'Geist Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace";

const spacing = {
  xs: 8,
  sm: 12,
  md: 20,
  lg: 32,
  sidebarWidth: 260,
  mainPadding: 32,
  cardPadding: 20,
  gap: 8,
  gridGap: 16,
  formGap: 16,
};

const motion = {
  fastMs: 150,
  normalMs: 250,
  slowMs: 350,
  easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
  keyframes: {
    fadeIn: 'opacity 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
    slideUp: 'translateY(4px) to translateY(0), opacity 0 to opacity 1, 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
    slideInRight: 'translateX(24px) to translateX(0), opacity 0 to opacity 1, 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    zoomIn: 'scale(0.96) to scale(1), opacity 0 to opacity 1, 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
  },
};

const baseTokens = {
  spacing,
  motion,
  shadow: {
    level1: '0 1px 2px rgba(0, 0, 0, 0.04)',
    level2: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
    level3: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06)',
  },
  radius: { lg: 12, md: 8, sm: 6, pill: 999, field: 8, button: 10 },
  typography: {
    displayStack: appFontFamily,
    bodyStack: appFontFamily,
    monoStack: appFontFamilyMono,
    weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
    uppercaseLetterSpacing: '0.06em',
    lineHeight: { tight: 1.25, base: 1.5, relaxed: 1.6 },
    antialiased: true,
  },
  layout: {
    pageMaxWidth: 1440,
    grids: {
      kpi: 'repeat(auto-fit, minmax(220px, 1fr))',
      twoCol: 'repeat(2, minmax(0, 1fr))',
      threeCol: 'repeat(3, minmax(0, 1fr))',
      form: 'repeat(2, minmax(0, 1fr))',
    },
    sidebar: { width: 260, padding: '16px', sticky: true },
    breakpoint: { value: 992, collapsedWidth: 56 },
  },
  iconography: {
    style: 'line',
    defaultSize: 16,
    navColor: '#71717A',
    hoverToPrimary: '#5551FF',
    kpiChip: { bg: '#EEF2FF', color: '#5551FF', radius: 10, size: 32 },
  },
  brand: {
    markSize: 46,
    markRadius: 10,
    markGradient: 'linear-gradient(180deg, #5551FF 0%, #4540F5 100%)',
    markGlow: '0 4px 14px rgba(85, 81, 255, 0.35)',
    markColor: '#5551FF',
    wordmarkFont: appFontFamily,
    wordmarkSize: 21,
    wordmarkWeight: 700,
  },
} as const;

export const lightTokens: DesignTokens = {
  ...baseTokens,
  color: {
    surfaces: {
      bg: '#FAFAFA',
      panel: '#FFFFFF',
      border: '#E4E4E7',
      borderStrong: '#D4D4D8',
      scrollbarThumb: '#D4D4D8',
      scrollbarThumbHover: '#A1A1AA',
      scrollbarTrack: '#FAFAFA',
    },
    text: { primary: '#0B0B0B', muted: '#52525B', label: '#71717A', sectionLabel: '#52525B' },
    primary: {
      base: '#5551FF',
      dark: '#4540F5',
      soft: '#EEF2FF',
      gradient: 'linear-gradient(180deg, #5551FF 0%, #4540F5 100%)',
      gradientStops: ['#5551FF', '#4540F5'],
      focusRing: 'rgba(85, 81, 255, 0.35)',
    },
    status: {
      success: { text: '#10B981', bg: '#ECFDF5' },
      warning: { text: '#F59E0B', bg: '#FFFBEB' },
      error: { text: '#DC2626', bg: '#FEF2F2' },
      info: { text: '#5551FF', bg: '#EEF2FF' },
    },
    badges: {
      default: { bg: '#F4F4F5', text: '#52525B' },
      info: { bg: '#EEF2FF', text: '#4540F5' },
      success: { bg: '#ECFDF5', text: '#047857' },
      warning: { bg: '#FFFBEB', text: '#B45309' },
      error: { bg: '#FEF2F2', text: '#B91C1C' },
      neutral: { bg: '#F4F4F5', text: '#71717A' },
    },
  },
  typography: {
    ...baseTokens.typography,
    sizes: { topbar: 24, modal: 16, kpi: 32, tableCell: 14, input: 14, label: 13, uppercaseLabel: 11 },
  },
  border: {
    width: 1,
    color: '#E4E4E7',
    input: '#D4D4D8',
    card: 'rgba(0, 0, 0, 0.06)',
  },
  focus: { ringColor: '#4540F5', ringWidth: 2, ringOffset: 1, ringAlpha: 0.2 },
  components: {
    nav: {
      itemBg: 'transparent',
      itemHoverBg: '#F4F4F5',
      itemActiveBg: '#EEF2FF',
      itemColor: '#0B0B0B',
      itemActiveColor: '#4540F5',
      itemIconColor: '#71717A',
      itemActiveShadow: 'inset 0 0 0 1px rgba(85, 81, 255, 0.12)',
      itemRadius: 8,
      itemPaddingInline: 12,
      itemMarginInline: 8,
    },
    button: {
      defaultBg: '#FFFFFF',
      defaultHoverBg: '#F4F4F5',
      primaryBg: '#5551FF',
      primaryHoverBg: '#4540F5',
      dangerBg: '#DC2626',
      ghostBg: 'transparent',
      radius: 10,
      fontWeight: 600,
    },
    input: { bg: '#FFFFFF', focusBg: '#FFFFFF' },
    table: {
      headerBg: '#F4F4F5',
      rowBg: '#FFFFFF',
      rowHoverBg: '#F4F4F5',
      lastRowBorder: '#E4E4E7',
      borderColor: '#E4E4E7',
    },
    badge: { baseBg: '#F4F4F5', dotSize: 6, radius: 999 },
    tab: {
      defaultColor: '#71717A',
      hoverColor: '#4540F5',
      activeColor: '#0B0B0B',
      underlineColor: '#5551FF',
      activeBg: '#EEF2FF',
    },
    pill: { bg: '#EEF2FF', text: '#4540F5', radius: 999 },
    avatar: { size: 32, bg: '#5551FF', text: '#FFFFFF' },
    kanban: {
      colBg: '#FAFAFA',
      colRadius: 12,
      cardBg: '#FFFFFF',
      cardHoverBg: '#F4F4F5',
      cardRadius: 10,
      cardShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      colHeaderColor: '#52525B',
    },
    timeline: {
      dotDone: '#10B981',
      dotInProgress: '#5551FF',
      dotBlocked: '#DC2626',
      dotOverdue: '#DC2626',
      dotPending: '#D4D4D8',
      lineColor: '#E4E4E7',
    },
    progress: { trackBg: '#E4E4E7', fillBg: '#5551FF' },
    checklist: {
      doneBg: '#ECFDF5',
      doneText: '#047857',
      pendingBg: '#F4F4F5',
      pendingText: '#52525B',
      radius: 6,
    },
    ocr: {
      dropzoneBg: '#FAFAFA',
      dropzoneBorder: '#D4D4D8',
      dropzoneHoverBg: '#EEF2FF',
      dropzoneText: '#52525B',
      dropzoneActiveBorder: '#5551FF',
    },
    alert: {
      successBg: '#ECFDF5',
      successBorder: '#A7F3D0',
      successText: '#047857',
      infoBg: '#EEF2FF',
      infoBorder: '#C7D2FE',
      infoText: '#4540F5',
      warningBg: '#FFFBEB',
      warningBorder: '#FDE68A',
      warningText: '#B45309',
      errorBg: '#FEF2F2',
      errorBorder: '#FECACA',
      errorText: '#B91C1C',
      radius: 10,
    },
    modal: {
      backdrop: 'rgba(9, 9, 11, 0.5)',
      surface: '#FFFFFF',
      padding: 20,
      maxWidth: 520,
      maxWidthLarge: 720,
      animation: motion.keyframes.zoomIn,
    },
    toast: {
      position: 'top center',
      successBg: '#ECFDF5',
      errorBg: '#FEF2F2',
      infoBg: '#EEF2FF',
      warningBg: '#FFFBEB',
      text: '#0B0B0B',
      radius: 10,
      animation: motion.keyframes.slideInRight,
    },
    scrollbar: { thumb: '#D4D4D8', thumbHover: '#A1A1AA', track: 'transparent', width: 8 },
    selection: { bg: 'rgba(85, 81, 255, 0.20)', text: 'inherit' },
    emptyState: { textColor: '#71717A', iconColor: '#A1A1AA' },
  },
};

export const darkTokens: DesignTokens = {
  ...baseTokens,
  color: {
    surfaces: {
      bg: '#09090B',
      panel: '#18181B',
      border: '#27272A',
      borderStrong: '#3F3F46',
      scrollbarThumb: '#3F3F46',
      scrollbarThumbHover: '#52525B',
      scrollbarTrack: '#09090B',
    },
    text: { primary: '#EDEDED', muted: '#A1A1AA', label: '#A1A1AA', sectionLabel: '#A1A1AA' },
    primary: {
      base: '#6C6BFF',
      dark: '#8180FF',
      soft: 'rgba(108, 107, 255, 0.12)',
      gradient: 'linear-gradient(180deg, #6C6BFF 0%, #8180FF 100%)',
      gradientStops: ['#6C6BFF', '#8180FF'],
      focusRing: 'rgba(108, 107, 255, 0.35)',
    },
    status: {
      success: { text: '#34D399', bg: 'rgba(52, 211, 153, 0.12)' },
      warning: { text: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)' },
      error: { text: '#EF4444', bg: 'rgba(239, 68, 68, 0.12)' },
      info: { text: '#6C6BFF', bg: 'rgba(108, 107, 255, 0.12)' },
    },
    badges: {
      default: { bg: '#27272A', text: '#A1A1AA' },
      info: { bg: 'rgba(108, 107, 255, 0.12)', text: '#A5A3FF' },
      success: { bg: 'rgba(52, 211, 153, 0.12)', text: '#34D399' },
      warning: { bg: 'rgba(245, 158, 11, 0.12)', text: '#FBBF24' },
      error: { bg: 'rgba(239, 68, 68, 0.12)', text: '#F87171' },
      neutral: { bg: '#27272A', text: '#A1A1AA' },
    },
  },
  typography: {
    ...baseTokens.typography,
    sizes: { topbar: 24, modal: 16, kpi: 32, tableCell: 14, input: 14, label: 13, uppercaseLabel: 11 },
  },
  shadow: {
    level1: '0 1px 2px rgba(0, 0, 0, 0.4)',
    level2: '0 4px 12px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3)',
    level3: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 6px rgba(0, 0, 0, 0.4)',
  },
  border: { width: 1, color: '#27272A', input: '#3F3F46', card: 'rgba(255, 255, 255, 0.06)' },
  focus: { ringColor: '#6C6BFF', ringWidth: 2, ringOffset: 1, ringAlpha: 0.25 },
  components: {
    nav: {
      itemBg: 'transparent',
      itemHoverBg: '#1F1F23',
      itemActiveBg: 'rgba(108, 107, 255, 0.12)',
      itemColor: '#EDEDED',
      itemActiveColor: '#A5A3FF',
      itemIconColor: '#A1A1AA',
      itemActiveShadow: 'inset 0 0 0 1px rgba(108, 107, 255, 0.18)',
      itemRadius: 8,
      itemPaddingInline: 12,
      itemMarginInline: 8,
    },
    button: {
      defaultBg: '#18181B',
      defaultHoverBg: '#1F1F23',
      primaryBg: '#6C6BFF',
      primaryHoverBg: '#8180FF',
      dangerBg: '#EF4444',
      ghostBg: 'transparent',
      radius: 10,
      fontWeight: 600,
    },
    input: { bg: '#18181B', focusBg: '#18181B' },
    table: {
      headerBg: '#1F1F23',
      rowBg: '#18181B',
      rowHoverBg: '#1F1F23',
      lastRowBorder: '#27272A',
      borderColor: '#27272A',
    },
    badge: { baseBg: '#27272A', dotSize: 6, radius: 999 },
    tab: {
      defaultColor: '#A1A1AA',
      hoverColor: '#A5A3FF',
      activeColor: '#EDEDED',
      underlineColor: '#6C6BFF',
      activeBg: 'rgba(108, 107, 255, 0.12)',
    },
    pill: { bg: 'rgba(108, 107, 255, 0.12)', text: '#A5A3FF', radius: 999 },
    avatar: { size: 32, bg: '#6C6BFF', text: '#FFFFFF' },
    kanban: {
      colBg: '#141416',
      colRadius: 12,
      cardBg: '#18181B',
      cardHoverBg: '#1F1F23',
      cardRadius: 10,
      cardShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
      colHeaderColor: '#A1A1AA',
    },
    timeline: {
      dotDone: '#34D399',
      dotInProgress: '#6C6BFF',
      dotBlocked: '#EF4444',
      dotOverdue: '#EF4444',
      dotPending: '#3F3F46',
      lineColor: '#27272A',
    },
    progress: { trackBg: '#27272A', fillBg: '#6C6BFF' },
    checklist: {
      doneBg: 'rgba(52, 211, 153, 0.12)',
      doneText: '#34D399',
      pendingBg: '#1F1F23',
      pendingText: '#A1A1AA',
      radius: 6,
    },
    ocr: {
      dropzoneBg: '#141416',
      dropzoneBorder: '#3F3F46',
      dropzoneHoverBg: 'rgba(108, 107, 255, 0.08)',
      dropzoneText: '#A1A1AA',
      dropzoneActiveBorder: '#6C6BFF',
    },
    alert: {
      successBg: 'rgba(52, 211, 153, 0.12)',
      successBorder: 'rgba(52, 211, 153, 0.3)',
      successText: '#34D399',
      infoBg: 'rgba(108, 107, 255, 0.12)',
      infoBorder: 'rgba(108, 107, 255, 0.3)',
      infoText: '#A5A3FF',
      warningBg: 'rgba(245, 158, 11, 0.12)',
      warningBorder: 'rgba(245, 158, 11, 0.3)',
      warningText: '#FBBF24',
      errorBg: 'rgba(239, 68, 68, 0.12)',
      errorBorder: 'rgba(239, 68, 68, 0.3)',
      errorText: '#F87171',
      radius: 10,
    },
    modal: {
      backdrop: 'rgba(0, 0, 0, 0.6)',
      surface: '#18181B',
      padding: 20,
      maxWidth: 520,
      maxWidthLarge: 720,
      animation: motion.keyframes.zoomIn,
    },
    toast: {
      position: 'top center',
      successBg: 'rgba(52, 211, 153, 0.14)',
      errorBg: 'rgba(239, 68, 68, 0.14)',
      infoBg: 'rgba(108, 107, 255, 0.14)',
      warningBg: 'rgba(245, 158, 11, 0.14)',
      text: '#EDEDED',
      radius: 10,
      animation: motion.keyframes.slideInRight,
    },
    scrollbar: { thumb: '#3F3F46', thumbHover: '#52525B', track: 'transparent', width: 8 },
    selection: { bg: 'rgba(108, 107, 255, 0.35)', text: 'inherit' },
    emptyState: { textColor: '#A1A1AA', iconColor: '#52525B' },
  },
};

export function getDesignTokens(mode: ColorMode): DesignTokens {
  return mode === 'dark' ? darkTokens : lightTokens;
}

export function getTokenPalette(tokens: DesignTokens): TokenPalette {
  const { color } = tokens;
  return {
    brand: color.primary.base,
    highlight: color.primary.dark,
    text: color.text.primary,
    muted: color.text.muted,
    label: color.text.label,
    sectionLabel: color.text.sectionLabel,
    link: color.primary.base,
    border: color.surfaces.border,
    fieldBorder: color.surfaces.borderStrong,
    borderStrong: color.surfaces.borderStrong,
    background: color.surfaces.bg,
    surface: color.surfaces.panel,
    shell: color.surfaces.panel,
    hover: tokens.components.table.headerBg,
    active: color.surfaces.border,
    selectedBg: color.primary.soft,
    focus: color.primary.dark,
    focusRing: color.primary.focusRing,
    error: color.status.error.text,
    warning: color.status.warning.text,
    success: color.status.success.text,
    info: color.status.info.text,
    errorBg: color.status.error.bg,
    warningBg: color.status.warning.bg,
    successBg: color.status.success.bg,
    infoBg: color.status.info.bg,
    readOnlyFieldBg: color.surfaces.panel,
    fieldShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06)',
    fieldHoverShadow: `inset 0 0 0 1px ${color.primary.focusRing}`,
  };
}

export const uxTokens = {
  spacing: {
    xs: spacing.xs,
    sm: spacing.sm,
    md: spacing.md,
    lg: spacing.lg,
  },
  motion: {
    fastMs: motion.fastMs,
    normalMs: motion.normalMs,
    easing: motion.easing,
  },
  layout: {
    pageMaxWidth: baseTokens.layout.pageMaxWidth,
    sidebarWidth: baseTokens.layout.sidebar.width,
    shellChromeRadius: baseTokens.radius.lg,
  },
} as const;

export const uiTransition = `all 0.2s cubic-bezier(0.16, 1, 0.3, 1)`;


export interface DesignTokens {
  color: {
    surfaces: {
      bg: string;
      panel: string;
      border: string;
      borderStrong: string;
      scrollbarThumb: string;
      scrollbarThumbHover: string;
      scrollbarTrack: string;
    };
    text: {
      primary: string;
      muted: string;
      label: string;
      sectionLabel: string;
    };
    primary: {
      base: string;
      dark: string;
      soft: string;
      gradient: string;
      gradientStops: [string, string];
      focusRing: string;
    };
    status: {
      success: { text: string; bg: string };
      warning: { text: string; bg: string };
      error: { text: string; bg: string };
      info: { text: string; bg: string };
    };
    badges: Record<BadgeVariant, { bg: string; text: string }>;
  };
  shadow: { level1: string; level2: string; level3: string };
  radius: { lg: number; md: number; sm: number; pill: number; field: number; button: number };
  spacing: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    sidebarWidth: number;
    mainPadding: number;
    cardPadding: number;
    gap: number;
    gridGap: number;
    formGap: number;
  };
  typography: {
    displayStack: string;
    bodyStack: string;
    monoStack: string;
    sizes: {
      topbar: number;
      modal: number;
      kpi: number;
      tableCell: number;
      input: number;
      label: number;
      uppercaseLabel: number;
    };
    weights: { regular: number; medium: number; semibold: number; bold: number };
    uppercaseLetterSpacing: string;
    lineHeight: { tight: number; base: number; relaxed: number };
    antialiased: boolean;
  };
  layout: {
    pageMaxWidth: number;
    grids: { kpi: string; twoCol: string; threeCol: string; form: string };
    sidebar: { width: number; padding: string; sticky: boolean };
    breakpoint: { value: number; collapsedWidth: number };
  };
  border: { width: number; color: string; input: string; card: string };
  focus: { ringColor: string; ringWidth: number; ringOffset: number; ringAlpha: number };
  components: {
    nav: {
      itemBg: string;
      itemHoverBg: string;
      itemActiveBg: string;
      itemColor: string;
      itemActiveColor: string;
      itemIconColor: string;
      itemActiveShadow: string;
      itemRadius: number;
      itemPaddingInline: number;
      itemMarginInline: number;
    };
    button: {
      defaultBg: string;
      defaultHoverBg: string;
      primaryBg: string;
      primaryHoverBg: string;
      dangerBg: string;
      ghostBg: string;
      radius: number;
      fontWeight: number;
    };
    input: { bg: string; focusBg: string };
    table: { headerBg: string; rowBg: string; rowHoverBg: string; lastRowBorder: string; borderColor: string };
    badge: { baseBg: string; dotSize: number; radius: number };
    tab: {
      defaultColor: string;
      hoverColor: string;
      activeColor: string;
      underlineColor: string;
      activeBg: string;
    };
    pill: { bg: string; text: string; radius: number };
    avatar: { size: number; bg: string; text: string };
    kanban: {
      colBg: string;
      colRadius: number;
      cardBg: string;
      cardHoverBg: string;
      cardRadius: number;
      cardShadow: string;
      colHeaderColor: string;
    };
    timeline: {
      dotDone: string;
      dotInProgress: string;
      dotBlocked: string;
      dotOverdue: string;
      dotPending: string;
      lineColor: string;
    };
    progress: { trackBg: string; fillBg: string };
    checklist: {
      doneBg: string;
      doneText: string;
      pendingBg: string;
      pendingText: string;
      radius: number;
    };
    ocr: {
      dropzoneBg: string;
      dropzoneBorder: string;
      dropzoneHoverBg: string;
      dropzoneText: string;
      dropzoneActiveBorder: string;
    };
    alert: {
      successBg: string;
      successBorder: string;
      successText: string;
      infoBg: string;
      infoBorder: string;
      infoText: string;
      warningBg: string;
      warningBorder: string;
      warningText: string;
      errorBg: string;
      errorBorder: string;
      errorText: string;
      radius: number;
    };
    modal: {
      backdrop: string;
      surface: string;
      padding: number;
      maxWidth: number;
      maxWidthLarge: number;
      animation: string;
    };
    toast: {
      position: string;
      successBg: string;
      errorBg: string;
      infoBg: string;
      warningBg: string;
      text: string;
      radius: number;
      animation: string;
    };
    scrollbar: { thumb: string; thumbHover: string; track: string; width: number };
    selection: { bg: string; text: string };
    emptyState: { textColor: string; iconColor: string };
  };
  motion: {
    fastMs: number;
    normalMs: number;
    slowMs: number;
    easing: string;
    keyframes: Record<string, string>;
  };
  iconography: {
    style: 'line';
    defaultSize: number;
    navColor: string;
    hoverToPrimary: string;
    kpiChip: { bg: string; color: string; radius: number; size: number };
  };
  brand: {
    markSize: number;
    markRadius: number;
    markGradient: string;
    markGlow: string;
    markColor: string;
    wordmarkFont: string;
    wordmarkSize: number;
    wordmarkWeight: number;
  };
}
